<template>
  <TitledSection class="contact-details" :align-content-left="true" icon-colour="inherit">
    <template v-slot:icon>
      <n-avatar :name="contact.name" :imageUrl="contact.avatarUrl" />
    </template>
    <template v-slot:title>
      <h2 class="title is-5">{{ contact.name || 'Unknown' }}</h2>
    </template>

    <n-data-table class="contact-details__body" :columns="columns" :items="data">
      <template v-slot:icon="{ row }">
        <Icon :icon="row.icon" />
      </template>
      <template v-slot:name="{ row }">
        <span class="contact-details__body__title">{{ row.name }}</span>
      </template>
      <template v-slot:value="{ row }">
        <EditableComponent
          v-model="row.value"
          :type="row.type"
          :options="row.options"
          :field-name="row.name"
          @update:model-value="(edited: ContactAttributeValue) => contactUpdated(row, edited)"
        ></EditableComponent>
      </template>
    </n-data-table>
  </TitledSection>
</template>

<script lang="ts" setup>
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import EditableComponent from '@/shared/components/EditableComponent.vue';
import AttributeIconUtil from '@/shared/util/attributeIconUtil';
import Icon from '@/shared/icons/Icon.vue';
import appInsights from '@/shared/services/appInsights';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';
import { computed, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useVModel } from '@vueuse/core';

type ContactAttributeValue = string | number | Date | undefined;

interface ContactAttributeRow {
  id: string;
  type: string;
  system: boolean;
  name: string;
  icon: string | undefined;
  value: ContactAttributeValue;
  options: string[];
}

const props = defineProps({
  modelValue: {
    type: Object as PropType<ContactViewModel>,
    required: true,
  },
});

const store = useStore();
const currentWorkspace = computed<WorkspaceViewModel>(() => store.getters.currentWorkspace);
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ContactViewModel): void;
}>();
const contact = useVModel(props, 'modelValue', emit);
const data = ref<ContactAttributeRow[]>([]);

const columns = [
  { name: 'icon', display: '' },
  { name: 'name', display: 'Name' },
  { name: 'value', display: 'Value' },
];

watch(
  contact,
  () => {
    const attributes: ContactAttributeViewModel[] = contactAttributes.value.filter(
      (a: ContactAttributeViewModel) => a.id !== 'name',
    );

    attributes.unshift({
      id: 'type',
      name: 'Type',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);
    attributes.unshift({
      id: 'surname',
      name: 'Last Name',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);
    attributes.unshift({
      id: 'givenName',
      name: 'First Name',
      type: ContactAttributeType.Text,
      system: true,
    } as ContactAttributeViewModel);

    data.value = [];
    attributes.forEach((a) =>
      data.value.push({
        id: a.id,
        type: getHtmlInputTypeForAttribute(a),
        system: a.system,
        name: a.name,
        icon: AttributeIconUtil.getIconType(a.type, a.id, a.system),
        value: getAttributeDisplayValue(a),
        options: a.name === 'Type' ? ['Lead', 'User'] : [],
      } as ContactAttributeRow),
    );
  },
  { immediate: true, deep: true },
);

function contactUpdated(row: ContactAttributeRow, value: any) {
  appInsights.trackEvent(
    { name: 'Contact details updated' },
    { workspaceId: currentWorkspace.value.id, field: row.id, value },
  );
  if (row.id === 'givenName') {
    contact.value.givenName = value;
  } else if (row.id === 'surname') {
    contact.value.surname = value;
  } else if (row.id === 'type') {
    contact.value.type = value.toLowerCase();
  } else if (row.id === 'email') {
    contact.value.email = value;
  } else if (row.id === 'phoneNumber') {
    contact.value.phoneNumber = value;
  } else if (row.id === 'firstSeen') {
    contact.value.firstSeenUtc = value;
  } else if (row.id === 'lastSeen') {
    contact.value.lastSeenUtc = value;
  } else if (row.id === 'lastContacted') {
    contact.value.lastContactedUtc = value;
  } else if (row.id === 'lastHeardFrom') {
    contact.value.lastHeardFromUtc = value;
  } else {
    contact.value.customAttributes[row.id] = value;
  }

  contact.value = { ...contact.value };
}

function getHtmlInputTypeForAttribute(attribute: ContactAttributeViewModel) {
  if (attribute.type === 'date' && attribute.system) {
    return 'datetime-local';
  }
  if (attribute.id === 'email') {
    return 'email';
  }

  if (attribute.id === 'phoneNumber') {
    return 'tel';
  }

  if (attribute.id === 'type') {
    return 'select';
  }
  return attribute.type;
}

function getAttributeDisplayValue(attribute: ContactAttributeViewModel): any {
  if (attribute.system) {
    return getSystemAttributeDisplayValue(attribute);
  } else {
    return getCustomAttributeDisplayValue(attribute);
  }
}

function getSystemAttributeDisplayValue(attribute: ContactAttributeViewModel) {
  if (attribute.type === ContactAttributeType.Date) {
    const value = (contact.value as any)[attribute.id + 'Utc'];
    if (value) {
      return new Date(value);
    }
    return value;
  } else {
    const value = (contact.value as any)[attribute.id];
    if (attribute.id === 'type') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  }
}

function getCustomAttributeDisplayValue(attribute: ContactAttributeViewModel) {
  const value = contact.value.customAttributes[attribute.id];
  if (attribute.type === ContactAttributeType.Date && value) {
    return new Date(value);
  }
  return value;
}
</script>

<style lang="scss" scoped>
.contact-details {
  :deep(.titled-section__heading) {
    .header {
      margin-left: 0.7em;
      padding: 1rem 0.25rem 0.25rem 0.25rem;
    }
  }
  :deep(.titled-section__content) {
    max-height: unset;
  }

  &__body {
    :deep(.n-table) {
      table-layout: fixed;
    }
    :deep(thead) {
      display: none;
    }

    :deep(.n-table td) {
      border: none;
      padding: 0.25em 0.125em;
      font-size: 0.8em;
      text-overflow: ellipsis;
      overflow: hidden;

      &:first-child {
        padding-left: 1em;
        width: 3em;
      }

      &:nth-child(2) {
        padding-right: 5px;
        width: 120px;
      }

      &.cell-wrap {
        word-break: break-word;
      }

      .contact-details__body__title {
        font-weight: bold;
      }
    }
  }
}
</style>
